import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../constants/api';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

type ProductCategory = 'chicken' | 'beef' | 'pork' | 'fish' | 'halal';

interface Product {
  title: string;
  memberPrice: string;
  regularPrice: string;
  image: string;
  qty: string;
  description: string;
}

interface ProductsData {
  [key: string]: Product[];
}

interface ProductsProps {
  category?: ProductCategory;
}

interface ProductModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

interface DataItem {
  name: string;
  generalOrder: number;
}

export const ProductModal: React.FC<ProductModalProps> = ({ product, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl mx-auto overflow-hidden">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 z-10 bg-white rounded-full p-1"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex flex-col md:flex-row">
            {/* Product image */}
            <div className="w-full md:w-1/2">
              <div className="h-72 md:h-full">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Product details */}
            <div className="w-full md:w-1/2 p-6 md:p-8">
              <div className="h-full flex flex-col">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{product.title}</h3>
                
                <div className="mb-6">
                  <p className="text-gray-600 leading-relaxed">{product.description}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-600">Quantity:</span>
                    <span className="font-medium">{product.qty}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-600">Regular price:</span>
                    <div className="text-red-500 font-bold">
                      {product.regularPrice}
                      <sup className="text-[10px] ml-0.5">BOX</sup>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Member price:</span>
                    <div className="text-custom-green font-bold">
                      {product.memberPrice}
                      <sup className="text-[10px] ml-0.5">BOX</sup>
                    </div>
                  </div>
                </div>

                <div className="mt-auto">
                  <button
                    onClick={() => window.location.href = '/booking'}
                    className="w-full bg-custom-red text-white px-6 py-3 rounded-full font-bold hover:bg-opacity-90 transition-colors text-lg"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { addToCart } = useCart();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({
      id: Math.random(), // Since your Product interface doesn't have id, we'll generate one
      title: product.title,
      price: product.regularPrice,
      regularPrice: product.regularPrice,
      image: product.image,
      category: 'default',
      quantity: 1
    });
  };

  return (
    <>
      <div className="bg-white rounded-lg overflow-hidden transition-transform hover:scale-105 p-4">
        {/* Make image clickable */}
        <div 
          onClick={() => setIsModalOpen(true)}
          className="cursor-pointer"
        >
          <img 
            src={product.image} 
            alt={product.title} 
            className="w-full h-48 object-cover" 
          />
        </div>
        {/* Make title clickable */}
        <h3 
          onClick={() => setIsModalOpen(true)}
          className="text-xl font-bold text-gray-900 mt-2 text-center cursor-pointer hover:text-custom-green transition-colors"
        >
          {product.title}
        </h3>
        <p className="text-gray-600 text-sm text-center mt-1">{product.qty}</p>
        <div className="space-y-0.5 text-center mt-1">
          <p>
            <span className="text-black">Regular price:</span>{' '}
            <span className="text-red-500 font-bold">{product.regularPrice}</span>{' '}
            <sup className="text-[10px] font-bold text-red-500">BOX</sup>
          </p>
          <p>
            <span className="text-black">Member price:</span>{' '}
            <span className="text-custom-green font-bold">{product.memberPrice}</span>{' '}
            <sup className="text-[10px] font-bold text-custom-green">BOX</sup>
          </p>
        </div>
        <div className="mt-4 space-y-2">
          <button 
            onClick={() => setIsModalOpen(true)}
            className="w-full bg-[#e81217] text-white py-2.5 rounded-md font-medium transition-all hover:bg-[#d11015] active:transform active:scale-[0.98]"
          >
            Learn More
          </button>
          <button
            onClick={handleAddToCart}
            className="w-full bg-black text-white py-2.5 rounded-md font-medium transition-all hover:bg-gray-900 active:transform active:scale-[0.98] flex items-center justify-center"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            <span>Add to Cart</span>
          </button>
        </div>
      </div>

      <ProductModal
        product={product}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

const Products: React.FC<ProductsProps> = ({ category = 'chicken' }) => {
  const [productsData, setProductsData] = useState<ProductsData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(API_ENDPOINTS.ALL_PRODUCTS);
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        
        // Transform the data to match the existing interface
        const transformedData: ProductsData = {
          beef: [],
          chicken: [],
          pork: [],
          fish: [],
          halal: [],
        };

        // Group products by parent category and transform the data structure
        data.forEach((item: any) => {
          const category = item.parentCategory.toLowerCase() as ProductCategory;
          const product: Product = {
            title: item.name,
            memberPrice: `${item.memberPrice} $`,
            regularPrice: `${item.regularPrice} $`,
            image: item.imageUrl,
            qty: item.quantity,
            description: item.description
          };
          
          if (transformedData[category]) {
            transformedData[category].push(product);
          }
        });

        // Sort each category by generalOrder
        Object.keys(transformedData).forEach(category => {
          transformedData[category].sort((a, b) => {
            const orderA = data.find((item: DataItem) => item.name === a.title)?.generalOrder || 0;
            const orderB = data.find((item: DataItem) => item.name === b.title)?.generalOrder || 0;
            return orderA - orderB;
          });
        });

        setProductsData(transformedData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred while fetching products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const products = productsData[category] || [];
  const categoryTitle = category.charAt(0).toUpperCase() + category.slice(1);

  if (isLoading) {
    return (
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-12"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg p-4">
                  <div className="h-48 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-red-500">
            <h2 className="text-2xl font-bold mb-4">Error Loading Products</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our {categoryTitle} Products</h2>
          <p className="text-lg text-gray-600">
            Premium quality {category} products from local farms
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {products.map((product, index) => (
            <ProductCard key={index} product={product} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Products;
